import React from 'react';
import { 
  FiEdit3, 
  FiFileText, 
  FiVideo, 
  FiCheckCircle,
  FiArrowRight,
  FiTrendingUp,
  FiMail,
  FiBookOpen
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { Image } from '../../components/ui/Image';
import { imageConfig } from '../../config/images';

export const ContentMarketingPage: React.FC = () => {
  const contentServices = [
    {
      title: 'Blog Writing & SEO',
      description: 'SEO-optimized blog content that drives organic traffic',
      icon: <FiEdit3 className="text-4xl" />,
      features: [
        'Keyword Research',
        'SEO-Optimized Articles',
        'Topic Ideation',
        'Content Calendar',
        'Internal Linking',
        'Performance Tracking'
      ],
      price: '₹5,000',
      duration: '/article (800-1200 words)'
    },
    {
      title: 'Website Copywriting',
      description: 'Conversion-focused copy for your website pages',
      icon: <FiFileText className="text-4xl" />,
      features: [
        'Homepage Copy',
        'Service Pages',
        'About Us',
        'Landing Pages',
        'Product Descriptions',
        'CTAs & Headlines'
      ],
      price: '₹8,000',
      duration: '/page'
    },
    {
      title: 'Video Content Production',
      description: 'Professional video content for social and web',
      icon: <FiVideo className="text-4xl" />,
      features: [
        'Script Writing',
        'Video Editing',
        'Motion Graphics',
        'YouTube Optimization',
        'Social Video Ads',
        'Video SEO'
      ],
      price: '₹15,000',
      duration: '/video (1-3 minutes)'
    },
    {
      title: 'Email Marketing Content',
      description: 'Engaging email campaigns that convert subscribers',
      icon: <FiMail className="text-4xl" />,
      features: [
        'Email Campaign Strategy',
        'Newsletter Writing',
        'Drip Campaign Creation',
        'Subject Line Testing',
        'Email Template Design',
        'Performance Analytics'
      ],
      price: '₹12,000',
      duration: '/month (4 campaigns)'
    },
    {
      title: 'Social Media Content',
      description: 'Engaging posts and graphics for social platforms',
      icon: <FiTrendingUp className="text-4xl" />,
      features: [
        'Post Writing',
        'Graphic Design',
        'Content Calendar',
        'Hashtag Strategy',
        'Platform Optimization',
        'Engagement Tracking'
      ],
      price: '₹18,000',
      duration: '/month (30 posts)'
    },
    {
      title: 'eBooks & Whitepapers',
      description: 'In-depth content for lead generation',
      icon: <FiBookOpen className="text-4xl" />,
      features: [
        'Research & Writing',
        'Professional Design',
        'Data Visualization',
        'PDF Optimization',
        'Landing Page Copy',
        'Promotion Strategy'
      ],
      price: '₹35,000',
      duration: '/eBook (10-20 pages)'
    }
  ];

  const contentPackages = [
    {
      name: 'Content Starter',
      price: '₹20,000',
      period: '/month',
      description: 'Essential content for growing brands',
      features: [
        '4 blog posts/month',
        '8 social media posts',
        'Basic keyword research',
        'Monthly content calendar',
        'Email support'
      ],
      popular: false
    },
    {
      name: 'Content Pro',
      price: '₹45,000',
      period: '/month',
      description: 'Comprehensive content marketing',
      features: [
        '8 blog posts/month',
        '20 social media posts',
        '2 email campaigns',
        'Advanced SEO strategy',
        'Content performance reports',
        'Dedicated content strategist'
      ],
      popular: true
    },
    {
      name: 'Content Enterprise',
      price: 'Custom',
      period: 'pricing',
      description: 'Complete content domination',
      features: [
        'Unlimited blog posts',
        'Full social media content',
        'Video content production',
        'eBooks & whitepapers',
        'Multi-platform strategy',
        'Dedicated content team',
        'Priority support'
      ],
      popular: false
    }
  ];

  const results = [
    {
      metric: '+250%',
      label: 'Organic Traffic Growth'
    },
    {
      metric: '+180%',
      label: 'Engagement Rate'
    },
    {
      metric: '+200%',
      label: 'Lead Generation'
    },
    {
      metric: '95%',
      label: 'Content Quality Score'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-32 bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-6xl font-bold mb-8">
            Content <span className="text-yellow-400">Marketing</span>
          </h1>
          <p className="text-2xl mb-12 max-w-4xl mx-auto opacity-90">
            Create compelling content that attracts, engages, and converts your 
            target audience into loyal customers.
          </p>
          
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-yellow-400 mb-2">{result.metric}</div>
                <div className="text-lg">{result.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Marketing Showcase */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Content That Connects & Converts</h2>
              <p className="text-lg text-gray-600 mb-8">
                We create compelling content that tells your brand story, engages your audience, 
                and drives measurable business results across all platforms.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <FiEdit3 className="text-purple-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Strategic Content Planning</h3>
                    <p className="text-gray-600">Data-driven content strategies aligned with your business goals</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <FiVideo className="text-purple-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Multi-Format Content</h3>
                    <p className="text-gray-600">Blogs, videos, infographics, and more to engage every audience</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <FiTrendingUp className="text-purple-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Performance Tracking</h3>
                    <p className="text-gray-600">Measure engagement and optimize for better results</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-8 shadow-2xl">
                <Image 
                  src={imageConfig.blog.featured1} 
                  alt="Content Marketing Examples" 
                  className="rounded-lg shadow-lg"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">+250%</div>
                  <div className="text-sm text-gray-600">Traffic Growth</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Content Marketing Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional content creation across all formats and platforms
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {contentServices.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 group">
                <div className="text-purple-600 mb-6 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3 text-gray-700">
                      <FiCheckCircle className="text-green-500 text-lg flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="border-t pt-6">
                  <div className="mb-4">
                    <div className="text-2xl font-bold text-purple-600">{service.price}</div>
                    <div className="text-sm text-gray-600">{service.duration}</div>
                  </div>
                  <Link
                    to="/contact"
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 group"
                  >
                    Get Started
                    <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Content Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Flexible content marketing packages to fit your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {contentPackages.map((pkg, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-2xl shadow-lg p-8 relative ${
                  pkg.popular ? 'border-2 border-purple-500 transform scale-105' : 'border border-gray-200'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-purple-600 mb-2">
                    {pkg.price}
                    <span className="text-lg text-gray-600 font-normal">{pkg.period}</span>
                  </div>
                  <p className="text-gray-600">{pkg.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <FiCheckCircle className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {pkg.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Why Choose Our Content Marketing Services?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We create content that engages, educates, and converts your audience
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiEdit3 className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Expert Writers</h3>
              <p className="text-gray-600 leading-relaxed">
                Professional content creators with expertise across multiple industries
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiTrendingUp className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">SEO-Optimized</h3>
              <p className="text-gray-600 leading-relaxed">
                Every piece of content optimized for search engines and user engagement
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiCheckCircle className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Multi-Format</h3>
              <p className="text-gray-600 leading-relaxed">
                Blogs, videos, infographics, eBooks, and more to reach every audience
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Marketing FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Content Marketing FAQs</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to know about our content services
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "What types of content do you create?",
                answer: "We create a wide range of content including blog posts, articles, website copy, social media content, email newsletters, video scripts, infographics, eBooks, whitepapers, case studies, and more. All content is customized to your brand voice and audience."
              },
              {
                question: "How do you ensure content quality?",
                answer: "Every piece goes through our quality process: research and planning, expert writing, SEO optimization, editorial review, and client approval. We also use plagiarism checkers and ensure all content is original, engaging, and valuable to your audience."
              },
              {
                question: "Can you write about technical or niche topics?",
                answer: "Absolutely! Our team includes writers with expertise across various industries including technology, healthcare, finance, manufacturing, and more. We research thoroughly to ensure accuracy and authority in any niche."
              },
              {
                question: "How long does it take to create content?",
                answer: "Turnaround time depends on content type and complexity. Blog posts typically take 5-7 days, website pages 7-10 days, and longer-form content like eBooks 3-4 weeks. We can accommodate rush requests with advance notice."
              },
              {
                question: "Do you handle content distribution?",
                answer: "Yes! We don't just create content - we help distribute it too. This includes publishing to your website, scheduling social media posts, email newsletter distribution, and promoting content through various channels for maximum reach."
              },
              {
                question: "How do you measure content performance?",
                answer: "We track metrics including page views, time on page, engagement rates, social shares, leads generated, SEO rankings, and conversions. You'll receive monthly reports showing how content drives traffic, engagement, and revenue."
              },
              {
                question: "Can you optimize existing content?",
                answer: "Definitely! We can audit your existing content, identify improvement opportunities, and refresh outdated pieces with new information, better SEO optimization, and improved readability to boost performance."
              },
              {
                question: "What's your content approval process?",
                answer: "We provide drafts for your review before publishing. You can request revisions until you're completely satisfied. Most clients are happy with the first draft, but we're flexible to ensure content meets your expectations."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="text-xl font-bold text-gray-800 cursor-pointer flex items-center justify-between group-hover:text-purple-600 transition-colors">
                  {faq.question}
                  <FiArrowRight className="transform group-open:rotate-90 transition-transform text-purple-600" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Tell Your Story?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Let's create content that resonates with your audience and drives 
            measurable results for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-yellow-400 text-gray-800 px-10 py-5 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-all duration-300 shadow-xl"
            >
              Get Content Strategy
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-white hover:text-purple-600 transition-all duration-300"
            >
              View Samples
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};